import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { 
  FileText,
  Plus,
  ArrowRight,
  Search,
  Download
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API = API_BASE;

const SummaryList = () => {
  const navigate = useNavigate();
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchSummaries();
  }, []);

  const fetchSummaries = async () => {
    try {
      const response = await axios.get(`${API}/credit-summaries`);
      setSummaries(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSummaries = summaries.filter(s =>
    s.client_info?.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    s.recommendation?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="summary-list-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] flex items-center gap-2">
            <FileText className="w-6 h-6 text-red-600" />
            Resumen y Decisión
          </h1>
          <p className="text-muted-foreground">Resúmenes ejecutivos de crédito</p>
        </div>
        <Button onClick={() => navigate('/apps/summary/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Resumen
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Buscar por cliente o recomendación..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {filteredSummaries.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSummaries.map((summary) => (
            <Card 
              key={summary.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/apps/summary/${summary.id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Resumen de Crédito</CardTitle>
                      <CardDescription>{summary.client_info?.full_name || 'Sin cliente'}</CardDescription>
                    </div>
                  </div>
                  <Badge 
                    variant={summary.recommendation === 'APPROVE' ? 'default' : summary.recommendation === 'DECLINE' ? 'destructive' : 'secondary'}
                  >
                    {summary.recommendation}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  {summary.appraisal_info && '\ud83d\ude9a Tasación '}
                  {summary.bank_analysis_info && '\ud83c\udfe6 Bank '}
                  {summary.credit_evaluation_info && '\ud83d\udcca Crédito '}
                  {summary.deal_structure_info && '\ud83d\udcb5 Deal'}
                </div>
                <div className="flex items-center text-sm text-primary font-medium pt-2">
                  Ver detalles
                  <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="py-12">
          <CardContent className="text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No hay resúmenes</h3>
            <p className="text-muted-foreground mb-4">Comienza creando tu primer resumen de crédito</p>
            <Button onClick={() => navigate('/apps/summary/new')}>
              <Plus className="w-4 h-4 mr-2" /> Nuevo Resumen
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SummaryList;
